import { useUserSetupStore } from '@sib/auth';
import { ICON_CIRCLE_STYLES } from '@sib/config';
import { FileUploadStore } from '@sib/files';
import { useTranslation } from '@sib/i18n';
import {
  FILE_MANAGER_DROPZONE_TEST_ID,
  CONFIRM_FILES_UPLOAD_TEST_ID,
} from '@sib/types';
import {
  Button,
  cn,
  Tooltip,
  useUploadingFilesList,
  UploadingFilesList,
} from '@sib/ui';
import { Upload } from '@sib/ui/icons';
import type { FileManagerConfig } from '@sib/ui/types';

interface FileManagerProps {
  closeFileManager: () => void;
  fileUploadStore: FileUploadStore;
  config: FileManagerConfig;
}

export const FileManager = ({
  closeFileManager,
  fileUploadStore,
  config,
}: FileManagerProps) => {
  const { t } = useTranslation();
  const {
    entityName,
    supportedFormats,
    maxFileSize,
    onSuccessNotificationAction,
    dropzoneConfig,
    disableBlobUpload,
    hideSensitiveActions,
    defaultDocumentInternal,
    UploadButtonProps,
    uploadFileCallback,
    auditUpload,
    organization,
  } = config;
  const {
    getRootProps,
    isDragActive,
    getInputProps,
    uploadFiles,
    areFilesUploading,
    canConfirmUpload,
    isMaxFilesReached,
    organizationId,
    organizationName,
    areThereAnyFiles,
  } = useUploadingFilesList({
    fileUploadStore,
    closeFileManager,
    notificationAction: onSuccessNotificationAction,
    dropzoneConfig,
    disableBlobUpload,
    uploadFileCallback,
    auditUpload,
    organization,
    prefetchDocumentMd5: !disableBlobUpload,
    defaultDocumentInternal,
  });
  const hasGlobalAccess = useUserSetupStore(store => store.hasGlobalAccess);

  const hasOrganizationContext = Boolean(organizationId || organizationName);

  return (
    <div className="flex h-full flex-col overflow-hidden">
      <div className="flex h-full w-full flex-col gap-7 overflow-hidden lg:flex-row">
        <div
          {...getRootProps({
            'data-testid': FILE_MANAGER_DROPZONE_TEST_ID,
          })}
          className={cn(
            'flex flex-1 flex-col items-center justify-center rounded-lg border-2 border-dashed p-2.5 text-center transition-colors sm:p-4',
            isMaxFilesReached
              ? 'border-muted-foreground/50 cursor-not-allowed opacity-60'
              : 'cursor-pointer',
            isDragActive && !isMaxFilesReached
              ? 'border-primary'
              : 'border-muted-foreground'
          )}
        >
          <input {...getInputProps()} />
          <Upload className={cn(ICON_CIRCLE_STYLES, 'text-primary')} />
          <div className="my-7">
            <p className="text-primary text-lg font-medium">
              {t('fileManager.drag', {
                entityName,
              })}
            </p>
            <p className="text-muted-foreground">
              {t('fileManager.supported', {
                formats: supportedFormats.join(', '),
              })}
            </p>
            <p className="text-muted-foreground">
              {t('fileManager.maxFileSize', {
                size: maxFileSize,
              })}
            </p>
          </div>
          <Button variant="outline" disabled={isMaxFilesReached}>
            {t('fileManager.browser')}
          </Button>
        </div>
        <UploadingFilesList
          fileUploadStore={fileUploadStore}
          hideSensitiveActions={hideSensitiveActions}
          defaultDocumentInternal={defaultDocumentInternal}
          dropzoneConfig={dropzoneConfig}
          prefetchDocumentMd5={!disableBlobUpload}
        />
      </div>
      <div className="mt-4 flex justify-center">
        <Tooltip
          content={
            hasOrganizationContext
              ? ''
              : hasGlobalAccess
                ? t('fileManager.tooltips.uploadDisabledNoOrganization')
                : t('fileManager.tooltips.uploadDisabledContactSupport')
          }
          TooltipTriggerProps={{ asChild: true }}
        >
          <span>
            <Button
              {...UploadButtonProps}
              onClick={uploadFiles}
              loading={areFilesUploading}
              disabled={
                !canConfirmUpload ||
                !areThereAnyFiles ||
                !hasOrganizationContext
              }
              data-testid={CONFIRM_FILES_UPLOAD_TEST_ID}
            >
              {UploadButtonProps?.label ||
                t('fileManager.uploadFilesForOrganization', {
                  name: organizationName,
                })}
            </Button>
          </span>
        </Tooltip>
      </div>
    </div>
  );
};
